import { createSlice } from "@reduxjs/toolkit";
import { getColorShades, getRgb, normalizeColor, getBrightness, color } from '@educaplay/core/utils'
import { openFullScreen, exitFullScreen } from '@educaplay/core/utils';

function customize(params) {
    const { primary, action, text, textAction, black, white, contrast, contrastDinamic } = params

    let shades;

    if (primary) {
        shades = getColorShades(primary)
        const brightness = getBrightness(primary)
        if (brightness > 90) {
            const darkColor = color(primary).shade(35).hexString()
            shades = getColorShades(darkColor)
        } else if (brightness < 10) {
            const lightColor = color(primary).tint(50).hexString()
            shades = getColorShades(lightColor)
        }
    }

    const content = `
        :root {
            ${primary ? `
                --color-primary-origin: ${primary};
                --color-primary-origin-rgb: ${getRgb(primary)};
                --color-primary: ${shades[7].hexString()};
                --color-primary-rgb: ${shades[7].rgb.join(', ')};
                
                --color-primary-light-7: ${shades[0].hexString()};
                --color-primary-light-7-rgb: ${shades[0].rgb.join(', ')};
                --color-primary-light-6: ${shades[1].hexString()};
                --color-primary-light-6-rgb: ${shades[1].rgb.join(', ')};
                --color-primary-light-5: ${shades[2].hexString()};
                --color-primary-light-5-rgb: ${shades[2].rgb.join(', ')};
                --color-primary-light-4: ${shades[3].hexString()};
                --color-primary-light-4-rgb: ${shades[3].rgb.join(', ')};
                --color-primary-light-3: ${shades[4].hexString()};
                --color-primary-light-3-rgb: ${shades[4].rgb.join(', ')};
                --color-primary-light-2: ${shades[5].hexString()};
                --color-primary-light-2-rgb: ${shades[5].rgb.join(', ')};
                --color-primary-light-1: ${shades[6].hexString()};
                --color-primary-light-1-rgb: ${shades[6].rgb.join(', ')};
                --color-primary-dark-1: ${shades[8].hexString()};
                --color-primary-dark-1-rgb: ${shades[8].rgb.join(', ')};
                --color-primary-dark-2: ${shades[9].hexString()};
                --color-primary-dark-2-rgb: ${shades[9].rgb.join(', ')};
                --color-primary-dark-3: ${shades[10].hexString()};
                --color-primary-dark-3-rgb: ${shades[10].rgb.join(', ')};
                --color-primary-dark-4: ${shades[11].hexString()};
                --color-primary-dark-4-rgb: ${shades[11].rgb.join(', ')};
                --color-primary-dark-5: ${shades[12].hexString()};
                --color-primary-dark-5-rgb: ${shades[12].rgb.join(', ')};
                --color-primary-dark-6: ${shades[13].hexString()};
                --color-primary-dark-6-rgb: ${shades[13].rgb.join(', ')};
                --color-primary-dark-7: ${shades[14].hexString()};
                --color-primary-dark-7-rgb: ${shades[14].rgb.join(', ')};
            
                --color-text: ${text};
                --color-text-rgb: ${getRgb(text)};
            `: ''}

            ${action ? `
                --color-action: ${action};
                --color-action-rgb: ${getRgb(action)};

                --color-text-action: ${textAction};
                --color-text-action-rgb: ${getRgb(textAction)};
            ` : ''}

            --color-black: ${black};
            --color-black-rgb: ${getRgb(black)};
            --color-white: ${white};
            --color-white-rgb: ${getRgb(white)};

            --color-contrast: ${contrast};
            --color-contrast-rgb: ${getRgb(contrast)};

            --color-contrast-blackOrigin: ${contrastDinamic};
            --color-contrast-blackOrigin-rgb: ${getRgb(contrastDinamic)};
        
            --color-contrast-whiteOrigin: ${contrastDinamic};
            --color-contrast-whiteOrigin-rgb: ${getRgb(contrastDinamic)};

        }
    `;

    const styleElement = document.querySelector('[data-id="game-styles"]')
    if (styleElement) {
        styleElement.innerHTML = content
    } else {
        const styles = document.createElement('style')
        styles.dataset.id = 'game-styles'
        styles.innerHTML = content
        document.head.appendChild(styles);
    }
}

const initialState = {
    ads: false,
    rating: {
        url: null
    },
    resources: '',
    login: {
        url: ''
    },
    customizations: {
        colors: {},
        backgroundImage: { src: null, showGame: true },
        customLogo: null,
        customBarLogo: null
    },
    isOpen: false,
    isFullscreen: false
}

const settingsSlice = createSlice({
    initialState,
    name: 'settings',
    reducers: {
        initSettings(state, action) {
            const { customizations, settings } = action.payload
            const { ads, rating, resources, login } = settings
            const { customLogo, customBarLogo, colors, backgroundImage } = customizations

            if (colors.action || colors.primary) {
                customize({
                    black: "#333333",
                    white: "#FFFFFF",
                    contrast: "#333333",
                    contrastDinamic: colors.text ? normalizeColor(colors.text) : "#333333",
                    ...(colors.action && {
                        action: normalizeColor(colors.action),
                        textAction: normalizeColor(colors.textAction),
                    }),
                    ...(colors.primary && {
                        primary: normalizeColor(colors.primary),
                        text: normalizeColor(colors.text),
                    }),
                })
            }

            state.showUser = !!settings.login.url
            state.ads = ads
            state.rating = rating
            state.resources = resources
            state.login = login
            state.customizations = {
                colors,
                backgroundImage: { src: backgroundImage.src ? `${resources}${backgroundImage.src}` : null, showGame: backgroundImage.showGame },
                customLogo: customLogo ? `${resources}${customLogo}` : null,
                customBarLogo: customBarLogo ? `${resources}${customBarLogo}` : null,
                hasCustomizations: !!(customLogo || customBarLogo || colors.primary || colors.action || backgroundImage.src)
            }
        },
        toggleSettings(state, action) {
            state.isOpen = action.payload
        },
        toggleFullscreen(state, action) {
            if (state.isFullscreen === action.payload) return

            const isActive = action.payload
            const fullscreenAction = isActive ? openFullScreen : exitFullScreen
            fullscreenAction()

            state.isFullscreen = isActive
        }
    }
})


export const { initSettings, toggleSettings, toggleFullscreen } = settingsSlice.actions
export default settingsSlice.reducer